import React, { FormEvent, useEffect, useState } from 'react';

import { Button } from 'modules';
import { translate } from 'languages';

import { InputPinCode } from '../input/PinCode';
import { PopupWraper } from '../popup';

type ModalPinCodeProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (pinCode: string) => Promise<void> | void;
  isLoading?: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
};

export const ModalPinCode = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  title = translate('pin-code-title'),
  description = translate('pin-code-description'),
  confirmLabel = translate('confirm'),
  cancelLabel = translate('cancel'),
}: ModalPinCodeProps): JSX.Element | null => {
  const [pinCode, setPinCode] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    if (isOpen) {
      setPinCode('');
      setError('');
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const close = () => {
    setPinCode('');
    setError('');
    onClose();
  };
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pinCode.length !== 6) {
      setError(translate('pin-code-required'));
      return;
    }
    setError('');
    await onConfirm(pinCode);
  };
  return (
    <PopupWraper center title={title} onClose={close} style={{ maxWidth: 440 }}>
      <form className='ModalPinCode' onSubmit={submit} noValidate>
        <div className='ModalPinCode__lock' aria-hidden='true'>
          🔒
        </div>
        <p className='ModalPinCode__description'>{description}</p>
        <InputPinCode
          name='pinCode'
          value={pinCode}
          onChange={(value) => {
            setPinCode(value);
            if (error) setError('');
          }}
          onTouched={() => undefined}
          autoFocus
          isDisabled={isLoading}
        />
        <div
          className='ModalPinCode__error'
          role={error ? 'alert' : undefined}
          aria-live='polite'
        >
          {error}
        </div>
        <div className='ModalPinCode__actions'>
          <Button
            label={cancelLabel}
            buttonType='primary-outline'
            onClick={close}
          />
          <Button
            label={confirmLabel}
            buttonType='success'
            type='submit'
            isLoading={isLoading}
            disabled={isLoading || pinCode.length !== 6}
          />
        </div>
      </form>
    </PopupWraper>
  );
};
